import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI, announcementsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Users, Megaphone, Settings, BarChart3, 
  Shield, Eye, Zap, Crown, Loader2, Check, Mail, Video, Plus, Send, Trash2, Edit2, Image, Sparkles, Wand2, Bell
} from 'lucide-react';
import { toast } from 'sonner';

const AdminPanel = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Newsletter state
  const [newsletters, setNewsletters] = useState([]);
  const [newsletterDialogOpen, setNewsletterDialogOpen] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState(null);
  const [savingNewsletter, setSavingNewsletter] = useState(false);
  const [sendingNewsletter, setSendingNewsletter] = useState(null);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [newsletterForm, setNewsletterForm] = useState({
    subject: '',
    content: '',
    image_url: '',
    video_url: '',
    recipients: 'all'
  });
  
  // Site settings state
  const [siteSettings, setSiteSettings] = useState(null);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [videoPrompt, setVideoPrompt] = useState('');
  
  // Video Ads state
  const [videoAds, setVideoAds] = useState([]);
  const [videoAdsLoading, setVideoAdsLoading] = useState(false);

  // Announcements state
  const [announcementsList, setAnnouncementsList] = useState([]);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '', message: '', link: '', priority: 'normal'
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, usersRes, promosRes, settingsRes, newslettersRes, siteSettingsRes, videoAdsRes, announcementsRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers(),
        adminAPI.getPromotions(),
        adminAPI.getSettings(),
        adminAPI.getNewsletters().catch(() => ({ data: { newsletters: [] } })),
        adminAPI.getSiteSettings().catch(() => ({ data: {} })),
        adminAPI.getVideoAds().catch(() => ({ data: { video_ads: [] } })),
        announcementsAPI.getAll().catch(() => ({ data: { announcements: [] } }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setPromotions(promosRes.data.promotions);
      setSettings(settingsRes.data);
      setNewsletters(newslettersRes.data.newsletters || []);
      setSiteSettings(siteSettingsRes.data || {});
      setVideoAds(videoAdsRes.data.video_ads || []);
      setAnnouncementsList(announcementsRes.data.announcements || []);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const updateUserPlan = async (userId, plan) => {
    try {
      await adminAPI.updateUserPlan(userId, plan);
      toast.success('Plan updated');
      fetchAll();
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const toggleAdmin = async (userId, isAdmin) => {
    try {
      await adminAPI.toggleAdmin(userId, isAdmin);
      toast.success(isAdmin ? 'Admin access granted' : 'Admin access revoked');
      fetchAll();
    } catch (error) {
      toast.error('Failed to update admin status');
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}? This will permanently delete all their data including promotions, landing pages, leads, and messages.`)) {
      return;
    }
    try {
      await adminAPI.deleteUser(userId);
      toast.success(`User ${email} deleted successfully`);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const moderatePromotion = async (promoId, isActive) => {
    try {
      await adminAPI.moderatePromotion(promoId, isActive);
      toast.success(isActive ? 'Promotion activated' : 'Promotion deactivated');
      fetchAll();
    } catch (error) {
      toast.error('Failed to moderate promotion');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await adminAPI.updateSettings(settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Newsletter functions
  const resetNewsletterForm = () => {
    setNewsletterForm({
      subject: '',
      content: '',
      image_url: '',
      video_url: '',
      recipients: 'all'
    });
    setEditingNewsletter(null);
    setAiTopic('');
    setAiTone('professional');
  };

  const openNewsletterDialog = (newsletter = null) => {
    if (newsletter) {
      setEditingNewsletter(newsletter);
      setNewsletterForm({
        subject: newsletter.subject,
        content: newsletter.content,
        image_url: newsletter.image_url || '',
        video_url: newsletter.video_url || '',
        recipients: newsletter.recipients
      });
    } else {
      resetNewsletterForm();
    }
    setNewsletterDialogOpen(true);
  };

  const generateNewsletterContent = async () => {
    if (!aiTopic.trim()) {
      toast.error('Please enter a topic');
      return;
    }
    setGeneratingContent(true);
    try {
      const response = await adminAPI.generateNewsletterContent(aiTopic, aiTone);
      const generated = response.data.generated;
      setNewsletterForm(prev => ({
        ...prev,
        subject: generated.subject || prev.subject,
        content: generated.content || prev.content
      }));
      toast.success('Newsletter content generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGeneratingContent(false);
    }
  };

  const saveNewsletter = async (e) => {
    e.preventDefault();
    setSavingNewsletter(true);
    try {
      if (editingNewsletter) {
        await adminAPI.updateNewsletter(editingNewsletter.id, newsletterForm);
        toast.success('Newsletter updated');
      } else {
        await adminAPI.createNewsletter(newsletterForm);
        toast.success('Newsletter created');
      }
      setNewsletterDialogOpen(false);
      resetNewsletterForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save newsletter');
    } finally {
      setSavingNewsletter(false);
    }
  };

  const deleteNewsletter = async (id) => {
    if (!window.confirm('Delete this newsletter?')) return;
    try {
      await adminAPI.deleteNewsletter(id);
      toast.success('Newsletter deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete newsletter');
    }
  };

  const sendNewsletter = async (id) => {
    if (!window.confirm('Send this newsletter to all selected recipients?')) return;
    setSendingNewsletter(id);
    try {
      const response = await adminAPI.sendNewsletter(id);
      toast.success(response.data.message);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send newsletter');
    } finally {
      setSendingNewsletter(null);
    }
  };

  const resendNewsletter = async (id) => {
    if (!window.confirm('Resend this newsletter to all selected recipients?')) return;
    setSendingNewsletter(id);
    try {
      const response = await adminAPI.resendNewsletter(id);
      toast.success(response.data.message);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend newsletter');
    } finally {
      setSendingNewsletter(null);
    }
  };

  const editNewsletter = (newsletter) => {
    setNewsletterForm({
      subject: newsletter.subject,
      content: newsletter.content,
      recipients: newsletter.recipients,
      image_url: newsletter.image_url || '',
      video_url: newsletter.video_url || ''
    });
    setEditingNewsletter(newsletter);
    setNewsletterDialogOpen(true);
  };

  // Video generation
  const generatePromoVideo = async () => {
    if (!videoPrompt.trim()) {
      toast.error('Please enter a video prompt');
      return;
    }
    
    setGeneratingVideo(true);
    toast.info('Video generation started. This may take several minutes...');
    
    try {
      const response = await adminAPI.generatePromoVideo(videoPrompt);
      toast.success('Video generated successfully!');
      setSiteSettings(prev => ({ ...prev, promo_video_url: response.data.video_url, promo_video_status: 'ready' }));
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Video generation failed');
    } finally {
      setGeneratingVideo(false);
    }
  };

  const updatePromoVideoUrl = async (url) => {
    try {
      await adminAPI.updateSiteSettings({ promo_video_url: url });
      toast.success('Video URL updated');
      fetchAll();
    } catch (error) {
      toast.error('Failed to update video URL');
    }
  };

  // Announcement functions
  const resetAnnouncementForm = () => {
    setAnnouncementForm({ title: '', message: '', link: '', priority: 'normal' });
    setEditingAnnouncement(null);
  };

  const openAnnouncementDialog = (announcement = null) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setAnnouncementForm({
        title: announcement.title,
        message: announcement.message,
        link: announcement.link || '',
        priority: announcement.priority || 'normal',
      });
    } else {
      resetAnnouncementForm();
    }
    setAnnouncementDialogOpen(true);
  };

  const saveAnnouncement = async (e) => {
    e.preventDefault();
    setSavingAnnouncement(true);
    try {
      const payload = { ...announcementForm, link: announcementForm.link || null };
      if (editingAnnouncement) {
        await announcementsAPI.update(editingAnnouncement.id, payload);
        toast.success('Announcement updated');
      } else {
        await announcementsAPI.create(payload);
        toast.success('Announcement created');
      }
      setAnnouncementDialogOpen(false);
      resetAnnouncementForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save announcement');
    } finally {
      setSavingAnnouncement(false);
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await announcementsAPI.delete(id);
      toast.success('Announcement deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete announcement');
    }
  };

  const toggleAnnouncementActive = async (announcement) => {
    try {
      await announcementsAPI.update(announcement.id, { is_active: !announcement.is_active });
      toast.success(announcement.is_active ? 'Announcement hidden' : 'Announcement activated');
      fetchAll();
    } catch {
      toast.error('Failed to update announcement');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl text-white flex items-center gap-2">
          <Shield className="w-6 h-6 text-neon-mint" />
          Admin Panel
        </h1>
        <p className="text-gray-400">Manage users, promotions, and platform settings</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-neon-mint" />
              <span className="text-gray-400">Total Users</span>
            </div>
            <p className="font-mono font-bold text-3xl text-white">{stats?.total_users || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Megaphone className="w-5 h-5 text-neon-violet" />
              <span className="text-gray-400">Active Promos</span>
            </div>
            <p className="font-mono font-bold text-3xl text-white">{stats?.active_promotions || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Eye className="w-5 h-5 text-neon-amber" />
              <span className="text-gray-400">Total Views</span>
            </div>
            <p className="font-mono font-bold text-3xl text-white">{stats?.total_views || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-void-paper border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Crown className="w-5 h-5 text-neon-violet" />
              <span className="text-gray-400">Pro Users</span>
            </div>
            <p className="font-mono font-bold text-3xl text-white">
              {(stats?.plan_distribution?.pro || 0) + (stats?.plan_distribution?.power || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="bg-void-paper border border-white/10">
          <TabsTrigger value="users" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="promotions" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
            <Megaphone className="w-4 h-4 mr-2" />
            Promotions
          </TabsTrigger>
          <TabsTrigger value="newsletters" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
            <Mail className="w-4 h-4 mr-2" />
            Newsletters
          </TabsTrigger>
          <TabsTrigger value="video-ads" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
            <Video className="w-4 h-4 mr-2" />
            Video Ads
          </TabsTrigger>
          <TabsTrigger value="video" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
            <Video className="w-4 h-4 mr-2" />
            Promo Video
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="announcements" className="data-[state=active]:bg-neon-mint/20 data-[state=active]:text-neon-mint">
            <Bell className="w-4 h-4 mr-2" />
            Announcements
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="bg-void-paper border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-heading">User Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-500 text-sm border-b border-white/10">
                      <th className="pb-3 font-medium">User</th>
                      <th className="pb-3 font-medium">Email</th>
                      <th className="pb-3 font-medium">Credits</th>
                      <th className="pb-3 font-medium">Plan</th>
                      <th className="pb-3 font-medium">Admin</th>
                      <th className="pb-3 font-medium">Joined</th>
                      <th className="pb-3 font-medium">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-white/5">
                        <td className="py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-neon-mint to-neon-violet flex items-center justify-center text-white font-bold text-sm">
                              {u.name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <span className="text-white">{u.name}</span>
                          </div>
                        </td>
                        <td className="py-4 text-gray-400">{u.email}</td>
                        <td className="py-4">
                          <div className="flex items-center gap-1">
                            <Zap className="w-4 h-4 text-neon-mint" />
                            <span className="font-mono text-white">{u.credits}</span>
                          </div>
                        </td>
                        <td className="py-4">
                          <Select
                            value={u.plan}
                            onValueChange={(value) => updateUserPlan(u.id, value)}
                            disabled={u.id === user?.id}
                          >
                            <SelectTrigger className="w-24 h-8 bg-void border-white/10 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-void-paper border-white/10">
                              <SelectItem value="free">Free</SelectItem>
                              <SelectItem value="pro">Pro</SelectItem>
                              <SelectItem value="power">Power</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-4">
                          <Switch
                            checked={u.is_admin}
                            onCheckedChange={(checked) => toggleAdmin(u.id, checked)}
                            disabled={u.id === user?.id}
                          />
                        </td>
                        <td className="py-4 text-gray-500 text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteUser(u.id, u.email)}
                            disabled={u.id === user?.id}
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Promotions Tab */}
        <TabsContent value="promotions">
          <Card className="bg-void-paper border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-heading">Promotion Moderation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-500 text-sm border-b border-white/10">
                      <th className="pb-3 font-medium">Promotion</th>
                      <th className="pb-3 font-medium">Category</th>
                      <th className="pb-3 font-medium">Views</th>
                      <th className="pb-3 font-medium">Clicks</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promotions.map((promo) => (
                      <tr key={promo.id} className="border-b border-white/5">
                        <td className="py-4">
                          <div>
                            <p className="text-white font-medium truncate max-w-[200px]">{promo.title}</p>
                            <p className="text-gray-500 text-sm truncate max-w-[200px]">{promo.description}</p>
                          </div>
                        </td>
                        <td className="py-4">
                          <span className="px-2 py-1 bg-white/5 rounded text-gray-400 text-sm">
                            {promo.category}
                          </span>
                        </td>
                        <td className="py-4 font-mono text-white">{promo.views}</td>
                        <td className="py-4 font-mono text-white">{promo.clicks}</td>
                        <td className="py-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            promo.is_active 
                              ? 'bg-neon-mint/10 text-neon-mint' 
                              : 'bg-red-500/10 text-red-400'
                          }`}>
                            {promo.is_active ? 'Active' : 'Disabled'}
                          </span>
                        </td>
                        <td className="py-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => moderatePromotion(promo.id, !promo.is_active)}
                            className={promo.is_active ? 'text-red-400 hover:text-red-300' : 'text-neon-mint hover:text-neon-mint/80'}
                          >
                            {promo.is_active ? 'Disable' : 'Enable'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card className="bg-void-paper border-white/10">
            <CardHeader>
              <CardTitle className="text-white font-heading">Platform Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Credits Per View</Label>
                  <Input
                    type="number"
                    min={1}
                    value={settings?.credits_per_view || 1}
                    onChange={(e) => setSettings(prev => ({ ...prev, credits_per_view: parseInt(e.target.value) }))}
                    className="bg-void border-white/10"
                    data-testid="credits-per-view-input"
                  />
                  <p className="text-gray-500 text-xs">Credits users earn per view</p>
                </div>
                
                <div className="space-y-2">
                  <Label>View Timer (seconds)</Label>
                  <Input
                    type="number"
                    min={5}
                    max={60}
                    value={settings?.view_timer_seconds || 10}
                    onChange={(e) => setSettings(prev => ({ ...prev, view_timer_seconds: parseInt(e.target.value) }))}
                    className="bg-void border-white/10"
                    data-testid="view-timer-input"
                  />
                  <p className="text-gray-500 text-xs">How long users must view</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Daily View Limit</Label>
                  <Input
                    type="number"
                    min={10}
                    max={200}
                    value={settings?.daily_view_limit || 50}
                    onChange={(e) => setSettings(prev => ({ ...prev, daily_view_limit: parseInt(e.target.value) }))}
                    className="bg-void border-white/10"
                    data-testid="daily-limit-input"
                  />
                  <p className="text-gray-500 text-xs">Max views per user per day</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Daily Login Bonus</Label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={settings?.daily_login_bonus || 5}
                    onChange={(e) => setSettings(prev => ({ ...prev, daily_login_bonus: parseInt(e.target.value) }))}
                    className="bg-void border-white/10"
                    data-testid="daily-bonus-input"
                  />
                  <p className="text-gray-500 text-xs">Credits for daily login</p>
                </div>
              </div>
              
              <Button 
                onClick={saveSettings}
                disabled={savingSettings}
                className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
                data-testid="save-settings-btn"
              >
                {savingSettings ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Newsletters Tab */}
        <TabsContent value="newsletters">
          <Card className="bg-void-paper border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white font-heading">Newsletters</CardTitle>
              <Button 
                onClick={() => openNewsletterDialog()}
                className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Newsletter
              </Button>
            </CardHeader>
            <CardContent>
              {newsletters.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500">No newsletters yet. Create your first one!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {newsletters.map((newsletter) => (
                    <div key={newsletter.id} className="flex items-center justify-between p-4 bg-void rounded-lg">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium truncate">{newsletter.subject}</h4>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            newsletter.status === 'sent' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {newsletter.status === 'sent' ? 'Sent' : 'Draft'}
                          </span>
                          <span className="text-gray-500 text-sm">
                            To: {newsletter.recipients === 'all' ? 'Everyone' : newsletter.recipients === 'users' ? 'Users' : 'Leads'}
                          </span>
                          {newsletter.sent_count > 0 && (
                            <span className="text-gray-500 text-sm">{newsletter.sent_count} recipients</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {newsletter.status !== 'sent' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => sendNewsletter(newsletter.id)}
                            disabled={sendingNewsletter === newsletter.id}
                            className="text-neon-mint hover:text-neon-mint/80"
                            title="Send"
                          >
                            {sendingNewsletter === newsletter.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => resendNewsletter(newsletter.id)}
                            disabled={sendingNewsletter === newsletter.id}
                            className="text-neon-amber hover:text-neon-amber/80"
                            title="Resend"
                          >
                            {sendingNewsletter === newsletter.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => editNewsletter(newsletter)}
                          className="text-gray-400 hover:text-white"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteNewsletter(newsletter.id)}
                          className="text-gray-400 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Newsletter Dialog */}
          <Dialog open={newsletterDialogOpen} onOpenChange={setNewsletterDialogOpen}>
            <DialogContent className="bg-void-paper border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {editingNewsletter ? 'Edit Newsletter' : 'Create Newsletter'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={saveNewsletter} className="space-y-4">
                {/* AI Content Generator */}
                {!editingNewsletter && (
                  <div className="p-4 bg-gradient-to-r from-neon-violet/10 to-neon-mint/10 rounded-lg border border-neon-violet/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Wand2 className="w-5 h-5 text-neon-violet" />
                      <span className="font-medium text-white">AI Content Generator</span>
                    </div>
                    <div className="space-y-3">
                      <Input
                        value={aiTopic}
                        onChange={(e) => setAiTopic(e.target.value)}
                        placeholder="What's the newsletter about? (e.g., 'New feature launch', 'Weekly tips')"
                        className="bg-void border-white/10"
                      />
                      <div className="flex gap-2">
                        <Select value={aiTone} onValueChange={setAiTone}>
                          <SelectTrigger className="bg-void border-white/10 w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-void-paper border-white/10">
                            <SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="friendly">Friendly</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                            <SelectItem value="casual">Casual</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          onClick={generateNewsletterContent}
                          disabled={generatingContent || !aiTopic.trim()}
                          className="bg-neon-violet text-white hover:bg-neon-violet/90 flex-1"
                        >
                          {generatingContent ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                          Generate Content
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    value={newsletterForm.subject}
                    onChange={(e) => setNewsletterForm(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Newsletter subject..."
                    required
                    className="bg-void border-white/10"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Content (HTML supported)</Label>
                  <Textarea
                    value={newsletterForm.content}
                    onChange={(e) => setNewsletterForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Write your newsletter content here... HTML is supported."
                    required
                    rows={8}
                    className="bg-void border-white/10 resize-none font-mono text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Image URL (Optional)</Label>
                    <Input
                      type="url"
                      value={newsletterForm.image_url}
                      onChange={(e) => setNewsletterForm(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="https://example.com/image.jpg"
                      className="bg-void border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Video URL (Optional)</Label>
                    <Input
                      type="url"
                      value={newsletterForm.video_url}
                      onChange={(e) => setNewsletterForm(prev => ({ ...prev, video_url: e.target.value }))}
                      placeholder="https://youtube.com/watch?v=..."
                      className="bg-void border-white/10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Recipients</Label>
                  <Select
                    value={newsletterForm.recipients}
                    onValueChange={(value) => setNewsletterForm(prev => ({ ...prev, recipients: value }))}
                  >
                    <SelectTrigger className="bg-void border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-void-paper border-white/10">
                      <SelectItem value="all">All (Users + Leads)</SelectItem>
                      <SelectItem value="users">Registered Users Only</SelectItem>
                      <SelectItem value="leads">Captured Leads Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <DialogFooter className="pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setNewsletterDialogOpen(false)}
                    className="text-gray-400"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={savingNewsletter}
                    className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
                  >
                    {savingNewsletter ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : editingNewsletter ? (
                      'Update'
                    ) : (
                      'Create'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Video Ads Tab */}
        <TabsContent value="video-ads">
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-void-paper border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                      <Video className="w-5 h-5 text-yellow-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Pending</p>
                      <p className="text-white font-bold text-lg">{videoAds.filter(a => a.status === 'pending').length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-void-paper border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Approved</p>
                      <p className="text-white font-bold text-lg">{videoAds.filter(a => a.status === 'approved').length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-void-paper border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-neon-mint/20 flex items-center justify-center">
                      <Send className="w-5 h-5 text-neon-mint" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Sent</p>
                      <p className="text-white font-bold text-lg">{videoAds.filter(a => a.status === 'sent').length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-void-paper border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <Trash2 className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Rejected</p>
                      <p className="text-white font-bold text-lg">{videoAds.filter(a => a.status === 'rejected').length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Video Ads List */}
            <Card className="bg-void-paper border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading">Video Ad Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                {videoAds.length === 0 ? (
                  <div className="text-center py-12">
                    <Video className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500">No video ads submitted yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {videoAds.map((ad) => (
                      <div 
                        key={ad.id}
                        className={`p-4 rounded-lg border ${
                          ad.status === 'pending' ? 'bg-yellow-500/5 border-yellow-500/30' :
                          ad.status === 'approved' ? 'bg-green-500/5 border-green-500/30' :
                          ad.status === 'sent' ? 'bg-neon-mint/5 border-neon-mint/30' :
                          'bg-red-500/5 border-red-500/30'
                        }`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-white font-medium truncate">{ad.title}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                ad.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                ad.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                ad.status === 'sent' ? 'bg-neon-mint/20 text-neon-mint' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {ad.status.charAt(0).toUpperCase() + ad.status.slice(1)}
                              </span>
                            </div>
                            <p className="text-gray-400 text-sm mb-1">By: {ad.user_email}</p>
                            <a 
                              href={ad.youtube_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-neon-mint text-sm hover:underline truncate block"
                            >
                              {ad.youtube_url}
                            </a>
                            {ad.description && (
                              <p className="text-gray-500 text-sm mt-2 line-clamp-2">{ad.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span>Payment: {ad.payment_method === 'credits' ? '200 credits' : '$20'}</span>
                              <span>Status: {ad.payment_status}</span>
                              <span>Submitted: {new Date(ad.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          
                          {ad.status === 'pending' && ad.payment_status === 'paid' && (
                            <div className="flex items-center gap-2">
                              <Button
                                onClick={async () => {
                                  try {
                                    await adminAPI.approveVideoAd(ad.id);
                                    toast.success('Video ad approved');
                                    fetchAll();
                                  } catch (error) {
                                    toast.error('Failed to approve video ad');
                                  }
                                }}
                                size="sm"
                                className="bg-green-500 text-white hover:bg-green-600"
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                onClick={async () => {
                                  const reason = prompt('Rejection reason (optional):');
                                  try {
                                    await adminAPI.rejectVideoAd(ad.id, reason || 'Does not meet guidelines');
                                    toast.success('Video ad rejected and refunded');
                                    fetchAll();
                                  } catch (error) {
                                    toast.error('Failed to reject video ad');
                                  }
                                }}
                                size="sm"
                                variant="outline"
                                className="border-red-500 text-red-500 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                          
                          {ad.status === 'approved' && (
                            <span className="text-green-400 text-sm">Ready for next newsletter</span>
                          )}
                          
                          {ad.status === 'sent' && (
                            <span className="text-neon-mint text-sm">Included in newsletter</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Promo Video Tab */}
        <TabsContent value="video">
          <div className="space-y-6">
            {/* Current Video */}
            <Card className="bg-void-paper border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading">Current Promo Video</CardTitle>
              </CardHeader>
              <CardContent>
                {siteSettings?.promo_video_url ? (
                  <div className="space-y-4">
                    <div className="aspect-video rounded-lg overflow-hidden bg-void border border-white/10">
                      <video 
                        src={siteSettings.promo_video_url} 
                        controls 
                        className="w-full h-full"
                      />
                    </div>
                    <p className="text-gray-400 text-sm">Current URL: {siteSettings.promo_video_url}</p>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Video className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500">No promo video set. Generate one with AI or add a URL manually.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Manual URL Update */}
            <Card className="bg-void-paper border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading">Update Video URL</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="url"
                    value={siteSettings?.promo_video_url || ''}
                    onChange={(e) => setSiteSettings(prev => ({ ...prev, promo_video_url: e.target.value }))}
                    placeholder="https://your-video-url.mp4 or YouTube embed URL"
                    className="bg-void border-white/10 flex-1"
                  />
                  <Button
                    onClick={() => updatePromoVideoUrl(siteSettings?.promo_video_url)}
                    className="bg-neon-mint text-black hover:bg-neon-mint/90"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                </div>
                <p className="text-gray-500 text-sm">
                  You can upload a video to YouTube/Vimeo and paste the URL here, or use a direct video file URL.
                </p>
              </CardContent>
            </Card>

            {/* AI Video Generation */}
            <Card className="bg-gradient-to-r from-neon-violet/10 to-neon-mint/10 border-neon-violet/30">
              <CardHeader>
                <CardTitle className="text-white font-heading flex items-center gap-2">
                  <Video className="w-5 h-5 text-neon-violet" />
                  AI Video Generator (Sora 2)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Video Prompt</Label>
                  <Textarea
                    value={videoPrompt}
                    onChange={(e) => setVideoPrompt(e.target.value)}
                    placeholder="Describe the promo video you want... (e.g., 'Modern tech startup office with people collaborating, showing growth charts on screens, upbeat atmosphere')"
                    rows={3}
                    className="bg-void border-white/10 resize-none"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-gray-400 text-sm">
                    <p>Video generation takes 2-5 minutes</p>
                    <p className="text-neon-amber">Uses Emergent LLM Key credits</p>
                  </div>
                  <Button
                    onClick={generatePromoVideo}
                    disabled={generatingVideo || !videoPrompt.trim()}
                    className="bg-neon-violet text-white hover:bg-neon-violet/90 font-bold"
                  >
                    {generatingVideo ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Video className="w-4 h-4 mr-2" />
                        Generate Video
                      </>
                    )}
                  </Button>
                </div>
                {siteSettings?.promo_video_status === 'generating' && (
                  <div className="p-3 bg-neon-amber/10 border border-neon-amber/30 rounded-lg">
                    <p className="text-neon-amber text-sm">Video is being generated. Please wait...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Announcements Tab */}
        <TabsContent value="announcements">
          <Card className="bg-void-paper border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-neon-mint" />
                Announcements ({announcementsList.length})
              </CardTitle>
              <Button onClick={() => openAnnouncementDialog()} className="bg-neon-mint text-black hover:bg-neon-mint/80" data-testid="create-announcement-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Announcement
              </Button>
            </CardHeader>
            <CardContent>
              {announcementsList.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No announcements yet. Create one to notify your users.</p>
              ) : (
                <div className="space-y-3">
                  {announcementsList.map(a => (
                    <div key={a.id} className={`p-4 rounded-lg border ${a.is_active ? 'border-white/10 bg-void' : 'border-white/5 bg-void/50 opacity-60'}`} data-testid={`announcement-row-${a.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-white font-medium truncate">{a.title}</h4>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                              a.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                              a.priority === 'important' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-white/10 text-gray-400'
                            }`}>{a.priority}</span>
                            {!a.is_active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-700 text-gray-400">Hidden</span>}
                          </div>
                          <p className="text-gray-400 text-sm line-clamp-2">{a.message}</p>
                          {a.link && <p className="text-neon-violet text-xs mt-1 truncate">{a.link}</p>}
                          <p className="text-gray-600 text-xs mt-1">{new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Switch
                            checked={a.is_active}
                            onCheckedChange={() => toggleAnnouncementActive(a)}
                            data-testid={`toggle-announcement-${a.id}`}
                          />
                          <Button variant="ghost" size="sm" onClick={() => openAnnouncementDialog(a)} className="text-gray-400 hover:text-white" data-testid={`edit-announcement-${a.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteAnnouncement(a.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-announcement-${a.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Announcement Dialog */}
          <Dialog open={announcementDialogOpen} onOpenChange={(open) => { if (!open) { setAnnouncementDialogOpen(false); resetAnnouncementForm(); } }}>
            <DialogContent className="bg-void-paper border-white/10 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white">{editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={saveAnnouncement} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={announcementForm.title}
                    onChange={(e) => setAnnouncementForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. New Feature Released!"
                    required
                    className="bg-void border-white/10"
                    data-testid="announcement-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea
                    value={announcementForm.message}
                    onChange={(e) => setAnnouncementForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Write your announcement message..."
                    required
                    rows={4}
                    className="bg-void border-white/10 resize-none"
                    data-testid="announcement-message-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Link (optional)</Label>
                    <Input
                      type="url"
                      value={announcementForm.link}
                      onChange={(e) => setAnnouncementForm(prev => ({ ...prev, link: e.target.value }))}
                      placeholder="https://..."
                      className="bg-void border-white/10"
                      data-testid="announcement-link-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={announcementForm.priority} onValueChange={(val) => setAnnouncementForm(prev => ({ ...prev, priority: val }))}>
                      <SelectTrigger className="bg-void border-white/10" data-testid="announcement-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-void-paper border-white/10">
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="important">Important</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setAnnouncementDialogOpen(false); resetAnnouncementForm(); }} className="border-white/10 text-gray-300">Cancel</Button>
                  <Button type="submit" disabled={savingAnnouncement} className="bg-neon-mint text-black hover:bg-neon-mint/80" data-testid="save-announcement-btn">
                    {savingAnnouncement ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {editingAnnouncement ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPanel;
AdminPanel;
